// Sentence.tsx
import React, { useEffect, useState } from 'react'
import FocusedWordContent from './FocusedWord'
import axios from 'axios'
import LoadingSpinner from './LoadingSpinner'
import { getConfig } from './config'

interface SentenceProps {
      sentence: string
      dialect: string
      rephrase: string
      spellingGrammar: string
      onFinish: () => void
}
interface FocusedWordsData {
      Word: string
      Color: string
      Pre: string
      Stress: string
      Post: string
}

interface DialectRephraseResponse {
      rephrasedSentence?: string;
      vulgarityDetected: boolean;
      gibberishDetected: boolean;
}

interface FocusedWordContentResponse {
      Word: string;
      Color: string;
      Pre: string;
      Stress: string;
      Post: string;
      'Pronunciation 1': string;
      'Pronunciation 2': string;
      'Pronunciation 3': string;
      'Pronunciation 4': string;
      'Pronunciation 5': string;
      'Pronunciation 6': string;
}

interface ItemsResponse {
      focusedWords: FocusedWordContentResponse[];
      prompt: string;
      stressedCompoundNouns: string[];
      compoundNouns: string[];
}
const WORDSMITH_SERVER_URL_BASE = getConfig().WORDSMITH_SERVER_URL_BASE;

const Sentence: React.FC<SentenceProps> = ({
      sentence,
      rephrase,
      dialect,
      spellingGrammar,
      onFinish,
}) => {
      const [processedSentence, setProcessedSentence] = useState('')
      const [focusedWordsData, setFocusedWordsData] = useState<
            FocusedWordsData[]
      >([])
      const [stressedCompoundNoun, setStressedCompoundNoun] = useState<string[]>([])
      const [showChromeExtensionLink, setShowChromeExtensionLink] = useState<boolean>(false);
      const [errorMessage, setErrorMessage] = useState<string>('');
      const [loading, setLoading] = useState(false)

      const getContainerStyles = (): React.CSSProperties => {
            let baseStyles: React.CSSProperties = {
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  gap: '30px',
                  justifyContent: 'center',
                  alignItems: 'center',
            };

            return baseStyles;
      };
      useEffect(() => {
            setLoading(true);
            setErrorMessage('');  // Reset the error message at the start

            const words = sentence.trim().split(' ');
            const wordCount = words.length;
            if (wordCount > 100) {
                  setErrorMessage("The maximum allowed word count is 100. Please reduce the number of words.");
                  setLoading(false);
                  onFinish();
                  return;
            }

            if (wordCount === 1) {
                  setProcessedSentence("");
                  setLoading(false);
                  setShowChromeExtensionLink(true);
                  onFinish();
                  return;
            }

            const fetchData = async (retryCount: number = 0) => {
                  let data = null;
                  try {
                        const dialectRephraseResponse = await axios.post(
                              `${WORDSMITH_SERVER_URL_BASE}/dialectrephrase`,
                              {
                                    prompt: sentence,
                                    maxTokens: 2000,
                                    rephrase: rephrase,
                                    dialect: dialect,
                                    spellingGrammar: spellingGrammar
                              },
                              {
                                    headers: {
                                          Accept: '*',
                                          'Content-Type': 'application/json',
                                    },
                              }
                        );

                        data = dialectRephraseResponse.data as DialectRephraseResponse;
                        if (data.vulgarityDetected) {
                              setErrorMessage("Wordsmith detected vulgarity in that sentence. Please try again.");
                              setLoading(false);
                              onFinish();
                              return;

                        }
                        if (data.gibberishDetected) {
                              setErrorMessage("Wordsmith could not understand that. Please try again.");
                              setLoading(false);
                              onFinish();
                              return;
                        }
                  } catch (error) {
                        setErrorMessage("Failed to fetch data. Please try again.");

                  }

                  if (data === null) {
                        setLoading(false);
                        onFinish();
                        return;
                  }

                  try {

                        setProcessedSentence(data.rephrasedSentence || sentence);

                        const responseData = await axios.post(
                              `${WORDSMITH_SERVER_URL_BASE}/items`,
                              {
                                    prompt: data.rephrasedSentence || sentence,
                                    maxTokens: 2000,
                                    rephrase: rephrase,
                                    dialect: dialect,
                              },
                              {
                                    headers: {
                                          Accept: '*',
                                          'Content-Type': 'application/json',
                                    },
                              }
                        );

                        const itemsResponseData = responseData.data as ItemsResponse;
                        setFocusedWordsData(itemsResponseData.focusedWords);
                        setStressedCompoundNoun(itemsResponseData.stressedCompoundNouns);

                  } catch (error) {
                        if (retryCount <= 0) {
                              setTimeout(() => fetchData(retryCount + 1), 1000); // Retry after 1 second
                        } else {
                              console.error('Failed to fetch data after retrying once.', error);
                              setErrorMessage("Failed to fetch data. Please try again.");
                        }
                  } finally {
                        setLoading(false);
                        onFinish();
                  }
            };

            fetchData();
      }, []);
      return (
            <div
                  style={
                        getContainerStyles()
                  }
            >
                  {loading && <LoadingSpinner />}
                  {loading && processedSentence && (
                        <span style={{ fontSize: '20px', fontStyle: 'italic', animation: 'pulse is infinite' }}>
                              Pronunciation Marking Incoming...
                        </span>
                  )}
                  <div
                        style={{
                              display: 'flex',
                              flex: '1',
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              gap: '5px',
                              justifyContent: 'center',
                              alignItems: 'center',
                        }}
                  >

                        {
                              showChromeExtensionLink &&
                              <a href="https://chrome.google.com/webstore/detail/blue-canoe-pronunciation/pjnkabicgidoflkilpobciceedcamkpo" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                    <span>
                                          For single word lookups please install our Chrome extension Blue Canoe Pronunciation Dictionary.
                                    </span>
                              </a>
                        }
                        {
                              errorMessage &&
                              (
                                    <span style={{ fontSize: '20px', fontStyle: 'italic', color: 'red' }}>
                                          {errorMessage}
                                    </span>
                              )
                        }
                        {processedSentence !== '' &&
                              processedSentence.split(' ').map((word, i) => {
                                    const cleanedWord = word
                                          .replace(/^[^\w\s]|[^\w\s]$/g, '')
                                          .trim()
                                          .toLowerCase()


                                    const wordData = focusedWordsData.find(
                                          (w) =>
                                                w.Word.trim().toLowerCase() ===
                                                cleanedWord
                                    )
                                    let isStressedCompoundNoun = false;
                                    if (stressedCompoundNoun) {
                                          isStressedCompoundNoun = stressedCompoundNoun.find(
                                                (word) =>
                                                      word.trim().toLowerCase() ===
                                                      cleanedWord
                                          ) !== undefined
                                    }

                                    if (wordData) {
                                          console.log(cleanedWord, wordData)
                                          // Replace the placeholder with the actual icon component you want to use
                                          const color =
                                                wordData.Color.toLowerCase()
                                          const iconPath = `icons/${color}_button.png`
                                          const iconPlaceholder = (
                                                <img
                                                      src={iconPath}
                                                      alt={`${color} icon`}
                                                      style={{
                                                            width: '30px',
                                                            height: '30px',
                                                      }}
                                                />
                                          )
                                          return (
                                                <FocusedWordContent
                                                      originalWord={word.trim()}
                                                      wordData={wordData}
                                                      icon={iconPlaceholder}
                                                      isStressedCompoundNoun={isStressedCompoundNoun}
                                                      key={i}
                                                />
                                          )
                                    }
                                    return (
                                          <>
                                                <span
                                                      style={{
                                                            fontSize: '20px',
                                                      }}
                                                      key={i}
                                                >
                                                      {word + ' '}
                                                </span>
                                          </>
                                    )
                              })}
                  </div>



            </div>
      )
}

export default Sentence
